import { get, patch, post, remove } from './coreApi';

export type AccountRelationship =
  | 'lead'
  | 'prospect'
  | 'customer'
  | 'former-customer'
  | 'partner';

export type AccountSalesStage =
  | 'new'
  | 'contacted'
  | 'qualified'
  | 'discovery'
  | 'demo-scheduled'
  | 'demo-completed'
  | 'evaluation'
  | 'migration-assessment'
  | 'proposal'
  | 'negotiation'
  | 'won'
  | 'lost';

export interface AccountUserSummary {
  id: number;
  name: string;
  email?: string | null;
}

export interface AccountInstanceSummary {
  id: number;
  instanceUniqueId?: string | null;
  name: string;
  activeStatus?: string | null;
}

export interface AccountContact {
  id: number;
  accountId: number;
  firstName: string;
  lastName?: string | null;
  name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary: boolean;
  influence?: string | null;
  notes?: string | null;
}

export interface AccountOpportunity {
  id: number;
  accountId: number;
  name: string;
  status: 'open' | 'won' | 'lost' | 'paused';
  salesStage: AccountSalesStage;
  estimatedArr?: number | null;
  probability?: number | null;
  expectedCloseAt?: string | null;
  source?: string | null;
  notes?: string | null;
  lossReason?: string | null;
  wonAt?: string | null;
  lostAt?: string | null;
  owner?: AccountUserSummary | null;
}

export type AccountActivityType =
  | 'note'
  | 'email'
  | 'call'
  | 'meeting'
  | 'demo'
  | 'website-lead'
  | 'migration-assessment'
  | 'proposal'
  | 'follow-up'
  | 'stage-change'
  | 'contact-added'
  | 'opportunity-created'
  | 'opportunity-updated'
  | 'instance-linked'
  | 'instance-unlinked'
  | 'signal-added'
  | 'signal-resolved'
  | 'system';

export type AccountSignalType =
  | 'requirement'
  | 'objection'
  | 'pain-point'
  | 'buying-signal'
  | 'competitor'
  | 'decision-criterion'
  | 'migration-risk'
  | 'product-interest';

export interface AccountActivity {
  id: number;
  accountId: number;
  contactId?: number | null;
  opportunityId?: number | null;
  activityType: AccountActivityType;
  direction?: 'inbound' | 'outbound' | 'internal' | null;
  status?: string | null;
  subject?: string | null;
  summary?: string | null;
  occurredAt: string;
  source: string;
  sourceId?: string | null;
  metadata?: Record<string, unknown>;
  createdBy?: AccountUserSummary | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountSignal {
  id: number;
  accountId: number;
  activityId?: number | null;
  signalType: AccountSignalType;
  value: string;
  status: 'active' | 'resolved' | 'invalid';
  strength?: number | null;
  confidence?: number | null;
  source: string;
  metadata?: Record<string, unknown>;
  resolvedAt?: string | null;
  createdBy?: AccountUserSummary | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountGrowthIntelligence {
  readOnly: true;
  accountId: number;
  generatedAt: string;
  fit: { score: number; level: 'low' | 'medium' | 'high'; source: 'manual' | 'ezee' };
  intent: { score: number; level: 'low' | 'medium' | 'high' };
  dealRisk: { score: number; level: 'low' | 'medium' | 'high'; reasons: string[] };
  stage: AccountSalesStage;
  nextAction: { action: string; reason: string; priority: 'normal' | 'medium' | 'high' };
  blockers: Array<{ type: string; value: string }>;
  positiveSignals: Array<{ type: string; value: string }>;
  evidence: string[];
  summary: string;
}

export interface AccountActivityInput {
  activityType: AccountActivityType;
  direction?: 'inbound' | 'outbound' | 'internal' | null;
  status?: string | null;
  subject?: string | null;
  summary?: string | null;
  occurredAt?: string | null;
  contactId?: number | null;
  opportunityId?: number | null;
  source?: string;
  sourceId?: string | null;
  metadata?: Record<string, unknown>;
}

export interface AccountSignalInput {
  signalType: AccountSignalType;
  value: string;
  activityId?: number | null;
  strength?: number | null;
  confidence?: number | null;
  source?: string;
  metadata?: Record<string, unknown>;
}

export interface EliteCoreAccount {
  id: number;
  name: string;
  website?: string | null;
  relationship: AccountRelationship;
  salesStage: AccountSalesStage;
  customerState?: 'onboarding' | 'implementing' | 'live' | 'expanding' | null;
  healthStatus?: 'healthy' | 'attention' | 'at-risk' | null;
  source?: string | null;
  sourceDetail?: string | null;
  industry?: string | null;
  employeeCount?: number | null;
  currentSystem?: string | null;
  estimatedArr?: number | null;
  probability?: number | null;
  fitScore?: number | null;
  nextAction?: string | null;
  nextActionAt?: string | null;
  lossReason?: string | null;
  wonAt?: string | null;
  lostAt?: string | null;
  archivedAt?: string | null;
  tags: string[];
  primaryContact?: AccountContact | null;
  contactCount: number;
  openOpportunityCount: number;
  openPipelineArr: number;
  owner?: AccountUserSummary | null;
  instances: AccountInstanceSummary[];
  contacts?: AccountContact[];
  opportunities?: AccountOpportunity[];
  painPoints?: string[];
  objections?: string[];
  notes?: string | null;
  metadata?: Record<string, unknown>;
  activities?: AccountActivity[];
  signals?: AccountSignal[];
  intelligence?: AccountGrowthIntelligence | null;
  requirements?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AccountMeta {
  relationships: AccountRelationship[];
  salesStages: AccountSalesStage[];
  customerStates: Array<'onboarding' | 'implementing' | 'live' | 'expanding'>;
  healthStatuses: Array<'healthy' | 'attention' | 'at-risk'>;
  opportunityStatuses: Array<'open' | 'won' | 'lost' | 'paused'>;
  activityTypes: AccountActivityType[];
  activityDirections: Array<'inbound' | 'outbound' | 'internal'>;
  signalTypes: AccountSignalType[];
  signalStatuses: Array<'active' | 'resolved' | 'invalid'>;
}

export interface AccountInput {
  name?: string;
  website?: string | null;
  relationship?: AccountRelationship;
  salesStage?: AccountSalesStage;
  customerState?: string | null;
  healthStatus?: string | null;
  source?: string | null;
  sourceDetail?: string | null;
  industry?: string | null;
  employeeCount?: number | null;
  currentSystem?: string | null;
  estimatedArr?: number | null;
  probability?: number | null;
  fitScore?: number | null;
  ownerUserId?: number | null;
  nextAction?: string | null;
  nextActionAt?: string | null;
  painPoints?: string[];
  objections?: string[];
  tags?: string[];
  notes?: string | null;
}

export interface AccountContactInput {
  firstName: string;
  lastName?: string;
  title?: string;
  email?: string;
  phone?: string;
  isPrimary?: boolean;
  influence?: string;
  notes?: string;
}

export interface AccountOpportunityInput {
  name: string;
  status?: AccountOpportunity['status'];
  salesStage?: AccountSalesStage;
  estimatedArr?: number | null;
  probability?: number | null;
  expectedCloseAt?: string | null;
  ownerUserId?: number | null;
  source?: string | null;
  notes?: string | null;
  lossReason?: string | null;
}

export const getAccountMeta = async (): Promise<AccountMeta> => {
  return get<AccountMeta>('/command-center/accounts/meta');
};

export const listAccounts = async (params?: {
  search?: string;
  relationship?: string;
  salesStage?: string;
  ownerUserId?: number;
  includeArchived?: boolean;
  limit?: number;
}): Promise<EliteCoreAccount[]> => {
  const response = await get<{ accounts?: EliteCoreAccount[] }>('/command-center/accounts', { params });
  return Array.isArray(response.accounts) ? response.accounts : [];
};

export const getAccount = async (accountId: number): Promise<EliteCoreAccount> => {
  const response = await get<{ account: EliteCoreAccount }>(`/command-center/accounts/${accountId}`);
  return response.account;
};

export const createAccount = async (input: AccountInput): Promise<EliteCoreAccount> => {
  const response = await post<{ account: EliteCoreAccount }>('/command-center/accounts', input);
  return response.account;
};

export const updateAccount = async (
  accountId: number,
  input: AccountInput
): Promise<EliteCoreAccount> => {
  const response = await patch<{ account: EliteCoreAccount }>(
    `/command-center/accounts/${accountId}`,
    input
  );
  return response.account;
};

export const linkAccountInstance = async (
  accountId: number,
  instanceId: number
): Promise<EliteCoreAccount> => {
  const response = await post<{ account: EliteCoreAccount }>(
    `/command-center/accounts/${accountId}/instances/${instanceId}`
  );
  return response.account;
};

export const unlinkAccountInstance = async (
  accountId: number,
  instanceId: number
): Promise<EliteCoreAccount> => {
  const response = await remove<{ account: EliteCoreAccount }>(
    `/command-center/accounts/${accountId}/instances/${instanceId}`
  );
  return response.account;
};

export const createAccountContact = async (
  accountId: number,
  input: AccountContactInput
): Promise<AccountContact> => {
  const response = await post<{ contact: AccountContact }>(
    `/command-center/accounts/${accountId}/contacts`,
    input
  );
  return response.contact;
};

export const createAccountOpportunity = async (
  accountId: number,
  input: AccountOpportunityInput
): Promise<AccountOpportunity> => {
  const response = await post<{ opportunity: AccountOpportunity }>(
    `/command-center/accounts/${accountId}/opportunities`,
    input
  );
  return response.opportunity;
};

export const updateAccountOpportunity = async (
  accountId: number,
  opportunityId: number,
  input: Partial<AccountOpportunityInput>
): Promise<AccountOpportunity> => {
  const response = await patch<{ opportunity: AccountOpportunity }>(
    `/command-center/accounts/${accountId}/opportunities/${opportunityId}`,
    input
  );
  return response.opportunity;
};


export const listAccountActivities = async (
  accountId: number,
  limit = 150
): Promise<AccountActivity[]> => {
  const response = await get<{ activities?: AccountActivity[] }>(
    `/command-center/accounts/${accountId}/activities`,
    { params: { limit } }
  );
  return Array.isArray(response.activities) ? response.activities : [];
};

export const createAccountActivity = async (
  accountId: number,
  input: AccountActivityInput
): Promise<AccountActivity> => {
  const response = await post<{ activity: AccountActivity }>(
    `/command-center/accounts/${accountId}/activities`,
    input
  );
  return response.activity;
};

export const listAccountSignals = async (
  accountId: number,
  status: 'active' | 'resolved' | 'invalid' | 'all' = 'all'
): Promise<AccountSignal[]> => {
  const response = await get<{ signals?: AccountSignal[] }>(
    `/command-center/accounts/${accountId}/signals`,
    { params: { status } }
  );
  return Array.isArray(response.signals) ? response.signals : [];
};

export const createAccountSignal = async (
  accountId: number,
  input: AccountSignalInput
): Promise<AccountSignal> => {
  const response = await post<{ signal: AccountSignal }>(
    `/command-center/accounts/${accountId}/signals`,
    input
  );
  return response.signal;
};

export const updateAccountSignal = async (
  accountId: number,
  signalId: number,
  input: Partial<Pick<AccountSignal, 'value' | 'status' | 'strength' | 'confidence'>>
): Promise<AccountSignal> => {
  const response = await patch<{ signal: AccountSignal }>(
    `/command-center/accounts/${accountId}/signals/${signalId}`,
    input
  );
  return response.signal;
};

export const getAccountGrowthIntelligence = async (
  accountId: number
): Promise<AccountGrowthIntelligence> => {
  const response = await get<{ intelligence: AccountGrowthIntelligence }>(
    `/command-center/accounts/${accountId}/intelligence`
  );
  return response.intelligence;
};
